import { ReactNode, useState } from 'react'
import { Alert, Box, Button, Chip, Grid } from '@mui/material'
import { BoardguardPdfDownload, Paper, Typography } from '../../../components'
import { useOrderBoardguardUpdates } from '../../../context/notifications'
import {
    BoardguardChange,
    BoardguardTicket,
    formatBoardguardDate,
    formatNoShowSegment,
    getBoardguardDisplayStatus,
    getBoardguardStatusColor,
    getBoardguardStatusLabel,
    monitorOrderOnBoardguard,
    triggerBoardguardCheck,
} from '../../../utils/boardguard'

const CHANGE_SEVERITY: Record<BoardguardChange['severity'], 'info' | 'warning' | 'error'> = {
    info: 'info',
    warning: 'warning',
    critical: 'error',
}

const InfoRow = ({ label, children }: { label: string, children: ReactNode }) => (
    <Grid item xs={12}>
        <Grid container direction="row" alignItems="center">
            <Grid item xs={6}>
                <Typography fontWeight="bold">{label}</Typography>
            </Grid>
            <Grid item xs={6} textAlign="end">
                {children}
            </Grid>
        </Grid>
    </Grid>
)

export const BoardguardCard = ({ orderId, reservationCode, ticket: initialTicket, supported }: {
    orderId: number
    reservationCode?: string | null
    ticket?: BoardguardTicket | null
    supported?: boolean
}) => {
    const [ticket, setTicket] = useState<BoardguardTicket | null>(initialTicket ?? null)
    const [sourceTicket, setSourceTicket] = useState(initialTicket)
    const [busy, setBusy] = useState(false)

    if (sourceTicket !== initialTicket) {
        setSourceTicket(initialTicket)
        setTicket(initialTicket ?? null)
    }

    useOrderBoardguardUpdates((event) => {
        if (event.order_id === orderId) setTicket(event.boardguard_ticket)
    })

    if (!ticket && !supported) return null

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true)
        try {
            await action()
        } finally {
            setBusy(false)
        }
    }

    const status = getBoardguardDisplayStatus(ticket)

    return (
        <Paper elevation={1} style={{ marginTop: 30 }}>
            <Grid container padding={2} spacing={2}>
                <Grid item xs={12} marginBottom={2}>
                    <Typography textAlign={"center"} variant="h5">Boardguard</Typography>
                </Grid>
                {!ticket ? (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Esta emissão ainda não é monitorada. O Boardguard acompanha a reserva no site da companhia,
                            avisa sobre alterações e cancelamentos e disponibiliza o bilhete em PDF.
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={busy}
                            onClick={() => run(async () => {
                                const created = await monitorOrderOnBoardguard(orderId)
                                if (created) setTicket(created)
                            })}
                        >
                            Monitorar no Boardguard
                        </Button>
                    </Grid>
                ) : (
                    <>
                        <InfoRow label="Status:">
                            <Chip color={getBoardguardStatusColor(status)} label={getBoardguardStatusLabel(status)} />
                        </InfoRow>
                        <InfoRow label="Última checagem:">
                            <Typography>{formatBoardguardDate(ticket.last_checked_at)}</Typography>
                        </InfoRow>
                        {!ticket.archived && (
                            <InfoRow label="Próxima checagem:">
                                <Typography>{formatBoardguardDate(ticket.next_check_at)}</Typography>
                            </InfoRow>
                        )}
                        {ticket.last_error && (
                            <Grid item xs={12}>
                                <Alert severity="error">{ticket.last_error}</Alert>
                            </Grid>
                        )}
                        {!!ticket.no_show_segments?.length && (
                            <Grid item xs={12}>
                                <Alert severity="error">
                                    <strong>No-show capturado</strong>
                                    {ticket.no_show_segments.map((segment, index) => (
                                        <div key={index}>{formatNoShowSegment(segment)}</div>
                                    ))}
                                </Alert>
                            </Grid>
                        )}
                        {!!ticket.last_changes?.length && (
                            <Grid item xs={12}>
                                <Typography fontWeight="bold" gutterBottom>Últimas alterações:</Typography>
                                <Box className="flex flex-col" sx={{ gap: 1 }}>
                                    {ticket.last_changes.map((change, index) => (
                                        <Alert key={index} severity={CHANGE_SEVERITY[change.severity] ?? 'info'}>
                                            <strong>{change.title}</strong>
                                            {change.description ? ` — ${change.description}` : ''}
                                        </Alert>
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        {!ticket.pdf_ready && !ticket.archived && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    Aguardando a primeira checagem. Você será notificado quando o bilhete estiver pronto para download.
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Box className="flex" sx={{ gap: 1 }}>
                                <BoardguardPdfDownload
                                    orderId={orderId}
                                    reservationCode={reservationCode}
                                    disabled={!ticket.pdf_ready}
                                    fullWidth
                                />
                                {!ticket.archived && (
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        disabled={busy}
                                        onClick={() => run(() => triggerBoardguardCheck(orderId))}
                                    >
                                        Verificar agora
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>
        </Paper>
    )
}
